import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, Check } from "lucide-react";

const HERO_IMG =
    "https://customer-assets.emergentagent.com/job_783724d6-9715-4541-9cca-454ee00fdf83/artifacts/8powsrpl_IMG_0488.jpeg";

const STATS = [
    { big: "6", unit: "Months", label: "Or Less Until You Win" },
    { big: "100", unit: "%", label: "Personal Financial Backing" },
    { big: "1", unit: "Team", label: "Working Beside You" },
    { big: "∞", unit: "Loyalty", label: "No Expiration Date" },
];

const PILLARS = [
    {
        title: "I Put Money On The Table",
        body: "If you're short by only a little, I've got you. If the goal is massive and takes real resources — I'll fund it and stay in until it's done.",
    },
    {
        title: "I Move Fast",
        body: "No long waiting times. No endless years of struggling. You can hit your desire in six months or less. If it takes longer, I stay by your side.",
    },
    {
        title: "I Actually Care",
        body: "I want to see you win at life. I want you to prosper. You become a dear friend — someone I call family — not a client or a number.",
    },
    {
        title: "You Can Depend On Me",
        body: "I won't let you down. I'm someone you can call on, depend on, and trust. That's the only currency that matters between us.",
    },
];

const AboutHero = () => (
    <section className="relative pt-24 md:pt-36 pb-20 md:pb-28 px-6 md:px-12 overflow-hidden" data-testid="about-hero">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
            <div className="lg:col-span-7">
                <p className="text-xs uppercase tracking-[0.4em] text-[#FF1A1A] mb-6">— The Mission</p>
                <h1 className="font-heading uppercase text-white text-5xl sm:text-7xl md:text-8xl lg:text-[140px] leading-[0.88] tracking-tight">
                    Whatever
                    <br />
                    you <span className="text-[#FF1A1A]">desire</span>,
                    <br />
                    I will make
                    <br />
                    it happen.
                </h1>
                <p className="mt-10 text-zinc-300 text-lg md:text-xl max-w-2xl font-light leading-relaxed">
                    I will work with you and help you achieve anything you desire.
                    I will put my own money up on the table to back you and fund your
                    success and your dreams.
                </p>
            </div>
            <div className="lg:col-span-5 relative">
                <div className="relative aspect-[3/4] overflow-hidden border border-white/10">
                    <img src={HERO_IMG} alt="Elijah Golden" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute bottom-6 left-6 right-6">
                        <p className="font-heading uppercase text-white text-3xl md:text-4xl leading-none">
                            Elijah <span className="text-[#FF1A1A]">Golden</span>
                        </p>
                        <p className="text-xs uppercase tracking-[0.3em] text-zinc-300 mt-2">
                            Benefactor · Mentor · Backer
                        </p>
                    </div>
                </div>
            </div>
        </div>
    </section>
);

const QuestionsMarquee = () => (
    <section className="border-y border-white/10 py-20 md:py-28" data-testid="questions-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12 mb-12">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-4">— Ask Yourself</p>
            <h2 className="font-heading uppercase text-white text-4xl md:text-6xl leading-none">
                Which one is <span className="text-[#FF1A1A]">you?</span>
            </h2>
        </div>
        <Marquee speed={40} gradient={false} className="font-heading uppercase text-6xl md:text-8xl lg:text-9xl tracking-tight leading-none">
            <span className="mx-10 text-stroke-red">Do you want a new car?</span>
            <span className="mx-10 text-white">Be the next big rapper?</span>
            <span className="mx-10 text-stroke-red">A model?</span>
            <span className="mx-10 text-white">A movie star?</span>
            <span className="mx-10 text-stroke-red">A CEO of your own business?</span>
            <span className="mx-10 text-[#FF1A1A]">I'll help you get there.</span>
        </Marquee>
    </section>
);

const StatCell = ({ stat, index }) => {
    const borderRight = index < 3 ? "md:border-r border-white/10" : "";
    const borderBottom = index < 2 ? "border-b md:border-b-0 border-white/10" : "";
    const borderRightMobile = index === 0 || index === 2 ? "border-r border-white/10" : "";
    return (
        <div
            className={`p-8 md:p-12 ${borderRight} ${borderBottom} ${borderRightMobile}`}
            data-testid={`stat-${index}`}
        >
            <div className="flex items-baseline gap-2 mb-4">
                <span className="font-heading text-[#FF1A1A] text-6xl md:text-7xl lg:text-8xl leading-none">{stat.big}</span>
                <span className="font-heading text-white text-2xl md:text-3xl uppercase">{stat.unit}</span>
            </div>
            <p className="text-zinc-400 text-sm md:text-base uppercase tracking-wider">{stat.label}</p>
        </div>
    );
};

const StatsSection = () => (
    <section className="py-20 md:py-28 px-6 md:px-12" data-testid="stats-section">
        <div className="max-w-[1600px] mx-auto grid grid-cols-2 md:grid-cols-4 gap-0 border border-white/10">
            {STATS.map((s, i) => (
                <StatCell key={s.label} stat={s} index={i} />
            ))}
        </div>
    </section>
);

const PillarCard = ({ pillar, index }) => (
    <div
        className="group relative bg-[#111111] border border-white/10 p-10 md:p-12 transition-all duration-500 hover:border-[#FF1A1A] hover:-translate-y-2"
        data-testid={`pillar-${index}`}
    >
        <div className="flex items-start gap-6">
            <div className="flex-shrink-0 w-14 h-14 border-2 border-[#FF1A1A] flex items-center justify-center">
                <Check className="w-6 h-6 text-[#FF1A1A]" />
            </div>
            <div>
                <h3 className="font-heading uppercase text-white text-2xl md:text-3xl lg:text-4xl leading-tight mb-4 group-hover:text-[#FF1A1A] transition-colors">
                    {pillar.title}
                </h3>
                <p className="text-zinc-400 leading-relaxed">{pillar.body}</p>
            </div>
        </div>
    </div>
);

const PillarsSection = () => (
    <section className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]" data-testid="pillars-section">
        <div className="max-w-[1600px] mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-16">
                <div className="md:col-span-5">
                    <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-6">— How It Works</p>
                    <h2 className="font-heading uppercase text-white text-5xl md:text-7xl leading-[0.9]">
                        Four things
                        <br />
                        I <span className="text-[#FF1A1A]">guarantee.</span>
                    </h2>
                </div>
                <div className="md:col-span-7 md:col-start-7 flex items-end">
                    <p className="text-zinc-400 text-lg font-light leading-relaxed">
                        When you work with me, you will achieve your goals fast. No long waiting times.
                        No endless years of struggling. Here's exactly what you get.
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="pillars-grid">
                {PILLARS.map((p, i) => (
                    <PillarCard key={p.title} pillar={p} index={i} />
                ))}
            </div>
        </div>
    </section>
);

const ManifestoLetter = () => (
    <div className="space-y-8 text-lg md:text-xl text-zinc-200 font-light leading-relaxed">
        <p>
            Whatever you want or desire, <span className="text-[#FF1A1A] font-medium">I can make it happen.</span> I
            will work with you and help you achieve anything you desire. I will put my
            own money up on the table to back you and fund your success and your dreams.
        </p>
        <p className="text-white text-2xl md:text-3xl font-heading uppercase leading-tight tracking-tight">
            Do you want a new car?
            <br />
            Do you want to be the next big rapper?
            <br />
            Do you want to be a model or movie star?
            <br />
            Do you want to be a CEO of your own business?
        </p>
        <p>
            Whatever you desire, I will help you achieve it — because I care about you.
            I actually want to see you succeed. I want you to be successful. I want you
            to achieve your dreams. The only thing I want in return is our friendship to
            flourish, and your loyalty.
        </p>
        <p>
            You can depend on me. I won't let you down. I'm someone you can call on,
            depended on, and trust. If you're short by only a little bit of money and
            you need a boost, then count on me. If you have a massive goal that requires
            a lot of time and resources, then put your faith in me and my team.
        </p>
        <p>
            When you work with me, you will achieve your goals fast. No long waiting
            times. No endless years of struggling. You can achieve your desire and goals
            within six months or less — and if it takes longer, then I'm willing to
            invest in you and stay by your side and continue to put money behind your
            success until you achieve it.
        </p>
        <p className="text-white font-medium">
            I want to see you win at life. I want to see you prosper. You will always be
            a dear friend to me, someone I can call family. I will do everything I can to
            see you succeed and receive your desire.
        </p>
        <p className="font-heading uppercase text-[#FF1A1A] text-3xl md:text-5xl leading-none tracking-tight pt-6">
            So call me. Let's make your dreams a reality.
        </p>
    </div>
);

const ManifestoSection = () => (
    <section className="py-24 md:py-36 px-6 md:px-12" data-testid="manifesto-section">
        <div className="max-w-5xl mx-auto">
            <p className="text-xs uppercase tracking-[0.3em] text-[#FF1A1A] mb-8 text-center">— From Elijah</p>
            <h2 className="font-heading uppercase text-white text-4xl md:text-6xl lg:text-7xl leading-[0.95] text-center mb-16">
                A letter to the one
                <br />
                who <span className="text-[#FF1A1A]">dares</span> to ask.
            </h2>
            <ManifestoLetter />
            <div className="mt-16 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                    to="/contact"
                    className="group inline-flex items-center justify-center gap-3 bg-[#FF1A1A] text-black font-heading uppercase tracking-widest px-10 py-5 hover:bg-white transition-colors duration-300"
                    data-testid="manifesto-cta-contact"
                >
                    Reach Out Now
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
            </div>
        </div>
    </section>
);

const About = () => (
    <div className="bg-[#050505] text-white" data-testid="about-page">
        <AboutHero />
        <QuestionsMarquee />
        <StatsSection />
        <PillarsSection />
        <ManifestoSection />
    </div>
);

export default About;
